import { renderRulebookUserText } from "@next-platform/client-common-core";
import { Icon, IconSize, IconVariants } from "@next-platform/client-common-spicy-ui";
import type { PublicMeterView } from "@next-platform/packages-ts-types";
import { MeterType } from "@next-platform/packages-ts-types";
import React from "react";

import Trans from "@/components/Trans/Trans";

import { HUD_CAPS, HUD_SECONDARY } from "./hudClassNames";

/** Heading of the "raises" column: what moves the meter up, in the meter's own terms. */
type RaisesTitle = "raises" | "earn" | "earnedBy" | "timeMoves";

const RAISES_TITLES: Record<RaisesTitle, React.ReactNode> = {
  raises: <Trans i18nKey="chat:rulebook.hud.sheet.raises" defaults="Raises it" />,
  earn: <Trans i18nKey="chat:rulebook.hud.sheet.earn" defaults="Earn" />,
  earnedBy: <Trans i18nKey="chat:rulebook.hud.sheet.earnedBy" defaults="Earned by" />,
  timeMoves: <Trans i18nKey="chat:rulebook.hud.sheet.timeMoves" defaults="How time moves" />,
};

type TellListProps = {
  title: React.ReactNode;
  tells: string[];
  isPositive: boolean;
  characterName?: string;
};

const TellList = ({ title, tells, isPositive, characterName }: TellListProps) => (
  <div data-testid={isPositive ? "RulebookMeterTells-Raises" : "RulebookMeterTells-Lowers"} className="flex flex-col gap-1.5">
    <div className={HUD_CAPS}>{title}</div>
    {tells.map((tell) => (
      <div key={tell} className={`flex gap-1.5 text-[13px] leading-[1.4] ${HUD_SECONDARY}`}>
        <span className={`mt-0.5 inline-flex flex-none ${isPositive ? "text-green-9 dark:text-green-600" : "text-red-9 dark:text-red-9"}`}>
          <Icon icon={IconVariants.ARROW_UP} size={IconSize.XSMALL} className={isPositive ? undefined : "rotate-180"} />
        </span>
        {renderRulebookUserText({ text: tell, characterName })}
      </div>
    ))}
  </div>
);

/**
 * Column titles per meter type, as in the design: clocks explain how time moves (no "lowers"), counters are
 * earned (and, when spendable, spent), everything else raises and lowers.
 */
const resolveTellColumns = (meter: PublicMeterView): { raisesTitle: RaisesTitle; isLowersShown: boolean } => {
  if (meter.type === MeterType.CLOCK) {
    return { raisesTitle: "timeMoves", isLowersShown: false };
  }
  if (meter.type === MeterType.NUMBER) {
    return meter.spendable ? { raisesTitle: "earn", isLowersShown: true } : { raisesTitle: "earnedBy", isLowersShown: false };
  }
  return { raisesTitle: "raises", isLowersShown: true };
};

type RulebookMeterTellsProps = {
  meter: PublicMeterView;
  characterName?: string;
  "data-testid"?: string;
};

const RulebookMeterTells = ({ meter, characterName, "data-testid": testId = "RulebookMeterTells" }: RulebookMeterTellsProps) => {
  const { raisesTitle, isLowersShown } = resolveTellColumns(meter);
  const raises = meter.tells_users?.raises ?? [];
  const lowers = isLowersShown ? (meter.tells_users?.lowers ?? []) : [];

  if (raises.length === 0 && lowers.length === 0) {
    return null;
  }

  return (
    <div data-testid={testId} className="grid grid-cols-[repeat(auto-fit,minmax(min(150px,100%),1fr))] gap-3">
      {raises.length > 0 ? <TellList title={RAISES_TITLES[raisesTitle]} tells={raises} isPositive={true} characterName={characterName} /> : null}
      {lowers.length > 0 ? (
        <TellList
          title={<Trans i18nKey="chat:rulebook.hud.sheet.lowers" defaults="Lowers it" />}
          tells={lowers}
          isPositive={false}
          characterName={characterName}
        />
      ) : null}
    </div>
  );
};

export default React.memo(RulebookMeterTells);
